export function formatArchiveCalendarWeek(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const start = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date - start) / 86400000);
  const week = Math.ceil((days + start.getDay() + 1) / 7);
  return `KW ${week}`;
}

export function formatArchiveDate(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale || undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function ownerInitialsFromName(name) {
  const source = String(name || '').trim();
  if (!source) return '—';
  const parts = source.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function archiveTypeLabelKey(entityType) {
  const map = {
    commitments: 'UserProjects.archive.typeLabels.commitment',
    constraints: 'UserProjects.archive.typeLabels.constraint',
    milestones: 'UserProjects.archive.typeLabels.milestone',
    decisions: 'UserProjects.archive.typeLabels.decision',
  };
  return map[entityType] || 'UserProjects.archive.typeLabels.entry';
}

export function archiveStatusDotColor(entry) {
  if (entry?.entityType === 'commitments' && entry.status === 'not_done') {
    return '#EF4444';
  }
  return '#22C55E';
}

export function resolveArchiveCategoryLabel(category, t) {
  if (!category) return '—';
  const mockupKey = `UserProjects.constraintDialog.categories.${category}`;
  const mockupLabel = t(mockupKey);
  if (mockupLabel !== mockupKey) return mockupLabel;
  const legacyKey = `ProjectManagement.commitments.cause.categories.${category}`;
  const legacyLabel = t(legacyKey);
  return legacyLabel !== legacyKey ? legacyLabel : String(category).toUpperCase();
}

export function computeArchiveDurationWeeks(startValue, endValue) {
  if (!startValue || !endValue) return null;
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diffMs = Math.max(0, end.getTime() - start.getTime());
  const weeks = Math.max(1, Math.round(diffMs / (7 * 86400000)));
  return weeks;
}

export function buildArchiveEntryMeta(entry, { t, userLookup = {}, milestoneLookup = {} }) {
  const week = formatArchiveCalendarWeek(entry.sortDate);
  const ownerName = userLookup[entry.ownerUserId] || entry.ownerDisplayName || '—';
  const owner = ownerInitialsFromName(ownerName);
  const milestoneTitle = entry.milestoneTitle
    || (entry.milestoneId ? milestoneLookup[entry.milestoneId] : null);

  if (entry.entityType === 'commitments') {
    const statusKey = entry.status === 'done'
      ? 'UserProjects.archive.entry.doneWeek'
      : 'UserProjects.archive.entry.notDoneWeek';
    const base = t(statusKey, { week });
    if (milestoneTitle) {
      return `${base} · ${owner} · ${t('UserProjects.archive.entry.milestone', { title: milestoneTitle })}`;
    }
    return `${base} · ${owner}`;
  }

  if (entry.entityType === 'constraints') {
    const category = resolveArchiveCategoryLabel(entry.category, t);
    return `${t('UserProjects.archive.entry.resolvedWeek', { week })} · ${owner} · ${t('UserProjects.archive.entry.category', { category })}`;
  }

  if (entry.entityType === 'decisions') {
    return `${t('UserProjects.archive.entry.decidedWeek', { week })} · ${owner}`;
  }

  return `${t('UserProjects.archive.entry.doneWeek', { week })} · ${t('UserProjects.archive.entry.milestone', { title: entry.title })}`;
}
